/* Cart drawer behaviour:
   - open/close (triggers: [data-cart-open] anywhere, backdrop, Close, Escape),
     with scroll lock + focus handling like the fullscreen menu
   - all mutations (qty stepper, remove, bundle-nudge add) go through the AJAX
     cart API, then the drawer re-renders itself via the Section Rendering API
     (?sections=cart-drawer) — server Liquid stays the single source of markup
   - keeps every cart count on the page in sync ([data-cart-count] spans and
     the menu's [data-cart-roll] text-roll label)
   - `onni:cart:changed` (dispatched by product-page.js after an AJAX add)
     refreshes the drawer and opens it — adding to cart always shows the cart.
   All clicks are delegated on document so re-rendered markup needs no rebinding. */
(function () {
  var root = document.querySelector('[data-cart]');
  if (!root) return;

  var panel = root.querySelector('[data-cart-panel]');
  var lastFocused = null;
  var busy = false;

  function isOpen() {
    return root.classList.contains('is-open');
  }

  /* ---------- open / close ---------- */
  function open() {
    if (isOpen()) return;

    // the drawer sits above the fullscreen menu (z-index 1200 > 1000), so it
    // can open right on top of it — leave the menu open behind it

    lastFocused = document.activeElement;
    root.hidden = false;
    // force reflow so the slide-in runs from the hidden state
    void root.offsetWidth;
    root.classList.add('is-open', 'is-opening');
    setTimeout(function () { root.classList.remove('is-opening'); }, 1200);
    document.documentElement.style.overflow = 'hidden';

    var closeBtn = panel.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('is-open', 'is-opening');
    // keep scroll locked if another overlay (menu / signup) is still open
    if (!document.querySelector('[data-menu].is-open, [data-signup].is-open')) {
      document.documentElement.style.overflow = '';
    }

    var onEnd = function (e) {
      if (e.target !== panel) return;
      root.hidden = true;
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
    // fallback if transitionend doesn't fire (reduced motion / display quirks)
    setTimeout(function () { if (!isOpen()) root.hidden = true; }, 800);

    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ---------- counts elsewhere on the page ---------- */
  function updateCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.hidden = count === 0;
      el.textContent = '(' + count + ')';
    });
    document.querySelectorAll('[data-cart-roll]').forEach(function (el) {
      var label = el.dataset.cartRoll + (count > 0 ? ' (' + count + ')' : '');
      el.dataset.text = label;
      var face = el.querySelector('.onni-roll_face');
      if (face) face.textContent = label;
    });
  }

  /* ---------- re-render from the server ---------- */
  function refresh() {
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
        var next = doc.querySelector('[data-cart-panel]');
        if (!next) return;
        panel.innerHTML = next.innerHTML;
        panel.dataset.count = next.dataset.count;
        updateCounts(parseInt(next.dataset.count, 10) || 0);
      });
  }

  /* ---------- mutations ---------- */
  function showError(message) {
    var el = panel.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message || 'Something went wrong — please try again.';
    el.hidden = false;
  }

  function request(url, body) {
    if (busy) return;
    busy = true;
    root.classList.add('is-busy');

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        if (res.ok) return refresh();
        return res.json().then(function (err) {
          throw new Error(err.description || err.message || '');
        });
      })
      .catch(function (err) {
        showError(err && err.message);
      })
      .finally(function () {
        busy = false;
        root.classList.remove('is-busy');
      });
  }

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      open();
      return;
    }

    var closer = e.target.closest('[data-cart-close]');
    if (closer && root.contains(closer)) {
      // real links (empty-state "Shop the Kiuas") keep navigating
      close();
      return;
    }

    var step = e.target.closest('[data-cart-step]');
    if (step && root.contains(step)) {
      var stepper = step.closest('[data-cart-stepper]');
      var qty = (parseInt(stepper.dataset.qty, 10) || 0) + parseInt(step.dataset.cartStep, 10);
      request('/cart/change.js', { line: parseInt(stepper.dataset.line, 10), quantity: Math.max(0, qty) });
      return;
    }

    var remove = e.target.closest('[data-cart-remove]');
    if (remove && root.contains(remove)) {
      request('/cart/change.js', { line: parseInt(remove.dataset.line, 10), quantity: 0 });
      return;
    }

    var hint = e.target.closest('[data-cart-hint]');
    if (hint && root.contains(hint)) {
      request('/cart/add.js', { items: [{ id: parseInt(hint.dataset.variant, 10), quantity: 1 }] });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) close();
  });

  /* the product page added something — show it */
  document.addEventListener('onni:cart:changed', function () {
    refresh().then(open);
  });

  /* Barba navigation: the incoming page wipes over everything — snap shut
     instantly, no slide-out racing the page transition */
  document.addEventListener('onni:page:out', function () {
    if (!isOpen()) return;
    root.classList.remove('is-open', 'is-opening');
    root.hidden = true;
    document.documentElement.style.overflow = '';
  });

  window.OnniCart = { open: open, close: close, refresh: refresh };
})();
